import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, LogOut, Search, RefreshCw } from "lucide-react";
import { SERVICIOS } from "@/components/Servicios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLS = ["Fecha", "Tipo", "Nombre / Razón social", "NIT / Cédula", "Correo", "Teléfono", "Servicio", "Mensaje"];

const fmtFecha = (iso) =>
    new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });

const toCsv = (leads) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) =>
        [fmtFecha(l.created_at), l.tipo_persona, l.nombre, l.nit_cedula, l.correo, l.telefono, l.servicio_interes, l.mensaje].map(esc).join(";")
    );
    return "\uFEFF" + [COLS.join(";"), ...rows].join("\r\n");
};

const selectCls =
    "border border-white/15 bg-navy-deep px-4 py-3 text-sm text-[#F8F9FA] focus:border-gold focus:outline-none transition-colors";

export default function LeadsPanel({ token, onLogout }) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [servicio, setServicio] = useState("");
    const [tipo, setTipo] = useState("");
    const [q, setQ] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/leads`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { servicio: servicio || undefined, tipo_persona: tipo || undefined, q: q || undefined },
            });
            setLeads(data);
        } catch (err) {
            if (err?.response?.status === 401) onLogout();
        } finally {
            setLoading(false);
        }
    }, [token, servicio, tipo, q, onLogout]);

    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
    }, [load]);

    const exportCsv = () => {
        const blob = new Blob([toCsv(leads)], { type: "text/csv;charset=utf-8;" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `leads-as-consulting-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <div className="min-h-screen bg-navy px-6 py-10 text-[#F8F9FA] md:px-12" data-testid="leads-panel">
            <header className="mx-auto flex max-w-[1440px] flex-wrap items-end justify-between gap-6 border-b border-white/10 pb-8">
                <div>
                    <p className="mb-3 text-[11px] uppercase tracking-[0.3em] text-gold">AS Consulting S.A.S. · Panel privado</p>
                    <h1 className="font-display text-3xl font-medium tracking-tight sm:text-4xl">Contactos recibidos</h1>
                    <p className="mt-2 text-sm text-[#9CA3AF]" data-testid="leads-count">
                        {loading ? "Cargando…" : `${leads.length} registro${leads.length === 1 ? "" : "s"}`}
                    </p>
                </div>
                <div className="flex gap-3">
                    <button onClick={load} className="flex items-center gap-2 border border-white/15 px-4 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors hover:border-gold" data-testid="leads-refresh-button">
                        <RefreshCw size={14} /> Actualizar
                    </button>
                    <button onClick={exportCsv} disabled={!leads.length} className="flex items-center gap-2 bg-gold px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy transition-colors hover:bg-gold-soft disabled:opacity-50" data-testid="leads-export-button">
                        <Download size={14} /> Exportar CSV
                    </button>
                    <button onClick={onLogout} className="flex items-center gap-2 border border-white/15 px-4 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors hover:border-red-400 hover:text-red-400" data-testid="leads-logout-button">
                        <LogOut size={14} /> Salir
                    </button>
                </div>
            </header>

            <div className="mx-auto mt-8 flex max-w-[1440px] flex-wrap gap-4">
                <label className="relative flex-1 min-w-[240px]">
                    <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar por nombre, correo, NIT o teléfono…"
                        className={`${selectCls} w-full pl-11 placeholder:text-[#9CA3AF]/50`}
                        data-testid="leads-search-input"
                    />
                </label>
                <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={selectCls} style={{ colorScheme: "dark" }} data-testid="leads-tipo-filter">
                    <option value="">Todos los tipos</option>
                    <option value="Natural">Natural</option>
                    <option value="Jurídica">Jurídica</option>
                </select>
                <select value={servicio} onChange={(e) => setServicio(e.target.value)} className={`${selectCls} max-w-xs`} style={{ colorScheme: "dark" }} data-testid="leads-servicio-filter">
                    <option value="">Todos los servicios</option>
                    {SERVICIOS.map((s) => (
                        <option key={s.titulo} value={s.titulo}>{s.titulo}</option>
                    ))}
                </select>
            </div>

            <div className="mx-auto mt-8 max-w-[1440px] overflow-x-auto border border-white/10">
                <table className="w-full min-w-[1100px] text-left text-sm" data-testid="leads-table">
                    <thead className="bg-navy-deep text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">
                        <tr>{COLS.map((c) => <th key={c} className="px-4 py-4 font-medium">{c}</th>)}</tr>
                    </thead>
                    <tbody>
                        {!loading && leads.length === 0 && (
                            <tr><td colSpan={COLS.length} className="px-4 py-12 text-center text-[#9CA3AF]" data-testid="leads-empty">No hay contactos que coincidan.</td></tr>
                        )}
                        {leads.map((l) => (
                            <tr key={l.id} className="border-t border-white/10 align-top transition-colors hover:bg-white/[0.03]" data-testid={`lead-row-${l.id}`}>
                                <td className="whitespace-nowrap px-4 py-4 text-[#9CA3AF]">{fmtFecha(l.created_at)}</td>
                                <td className="px-4 py-4"><span className="border border-gold/40 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-gold">{l.tipo_persona}</span></td>
                                <td className="px-4 py-4 font-medium">{l.nombre}</td>
                                <td className="whitespace-nowrap px-4 py-4">{l.nit_cedula}</td>
                                <td className="px-4 py-4"><a href={`mailto:${l.correo}`} className="hover:text-gold">{l.correo}</a></td>
                                <td className="whitespace-nowrap px-4 py-4"><a href={`https://wa.me/57${l.telefono.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="hover:text-gold">{l.telefono}</a></td>
                                <td className="px-4 py-4 text-[#9CA3AF]">{l.servicio_interes}</td>
                                <td className="max-w-xs px-4 py-4 text-[#9CA3AF]">{l.mensaje || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
